import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, Pencil, Trash2 } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import Avatar from '../ui/Avatar';
import Button from '../ui/Button';
import CommentsColumn from '../ui/CommentsColumn';
import Composer from '../ui/Composer';
import Textarea from '../ui/Textarea';
import { useConfirm } from '../ui/Confirm';
import { useToast } from '../ui/Toast';
import { clean } from '../shell/copy';

// The review frame's comments column (DESIGN rev 2.1 §2.6 Review set, HD-8): src/ui CommentsColumn with numbered
// comment cards (number = place in timecode order), threaded replies, and the Composer pinned to the bottom.
// Used by the Review page (/review/:id, signed-in staff and client accounts) and the video share viewer (/share/:token).
// Same calls as before:
//   POST   /api/comments            { videoAssetId, timestampSeconds, text }            new comment (unless onCommentPost)
//   POST   /api/comments            { videoAssetId, timestampSeconds, text, parentId }  reply
//   PATCH  /api/comments/:id        { text }                                             edit (own)
//   DELETE /api/comments/:id                                                             delete (own; replies go with it)
//   PATCH  /api/comments/:id/resolve                                                     resolve / reopen
// manage = false (the guest share viewer): no reply, edit, delete or resolve; there is no guest route for them.
// slot: what sits where the composer goes when this viewer cannot post yet (a guest's name step) or at all (a notice).

const shortDate = iso => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '');
const nameOf = c => clean(c.commenter?.name || c.commenterName || 'Unknown');

function InlineEdit({ value, onChange, onSave, onCancel, busy, saveLabel, label }) {
  return (
    <div className="ui-stack ui-stack--tight">
      <Textarea aria-label={label} value={value} rows={2} autoFocus onChange={e => onChange(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
          if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); onSave(); }
        }} />
      <div className="ui-cluster ui-cluster--end">
        <Button variant="quiet" onClick={onCancel}>Cancel</Button>
        <Button variant="secondary" onClick={onSave} loading={busy} disabled={!value.trim()}>{saveLabel}</Button>
      </div>
    </div>
  );
}

export default function CommentPanel({
  videoAssetId,
  currentTimeSeconds,
  onSeekTo,
  comments,
  onCommentsChange,
  activeCommentId,
  canPost = true,
  identity = null,
  onCommentPost = null,
  slot = null,
  manage = true,
  showResolve = true,
  onTypingStart,
  details,
  format = 'mss',
  fps,
  emptyMessage,
}) {
  const { user } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const textareaRef = useRef(null);
  const [editing, setEditing] = useState(null);     // { id, text }
  const [replying, setReplying] = useState(null);   // { id, text }
  const [busy, setBusy] = useState(false);

  // Shift+C anywhere on the page (outside a field) puts the cursor in the composer, so review stays keyboard-only:
  // Space plays and pauses, Shift+C starts a comment, Enter sends, Space plays again.
  useEffect(() => {
    if (!canPost) return undefined;
    function handleHotkey(e) {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.shiftKey && (e.code === 'KeyC' || e.key === 'C' || e.key === 'c')) {
        e.preventDefault();
        textareaRef.current?.focus();
      }
    }
    document.addEventListener('keydown', handleHotkey, true);
    return () => document.removeEventListener('keydown', handleHotkey, true);
  }, [canPost]);

  const { cards, replies } = useMemo(() => {
    const top = comments.filter(c => !c.parentId).sort((a, b) => a.timestampSeconds - b.timestampSeconds);
    const byParent = {};
    comments.forEach(c => { if (c.parentId) (byParent[c.parentId] ||= []).push(c); });
    Object.values(byParent).forEach(xs => xs.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)));
    return {
      cards: top.map(c => ({ id: c.id, author: { name: nameOf(c), initials: c.commenter?.initials }, date: c.createdAt,
        at: Number(c.timestampSeconds), text: c.text, resolved: !!c.resolvedAt, raw: c })),
      replies: byParent,
    };
  }, [comments]);

  const isOwn = c => !!(user?.id && c.commenter?.id === user.id);

  async function handlePost({ text, at }) {
    try {
      if (onCommentPost) await onCommentPost({ timestampSeconds: at, text });
      else await api.post('/api/comments', { videoAssetId, timestampSeconds: at, text });
    } catch {
      throw new Error('The comment did not post. Check the connection and send it again.');
    }
    onCommentsChange();
    // Focus leaves the field, so the next Space reaches the player's shortcut instead of typing a space.
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
  }

  async function saveEdit() {
    if (!editing?.text.trim() || busy) return;
    setBusy(true);
    try {
      await api.patch(`/api/comments/${editing.id}`, { text: editing.text.trim() });
      onCommentsChange();
      setEditing(null);
    } catch { toast('The edit was not saved. Try again.', { tone: 'danger' }); }
    finally { setBusy(false); }
  }

  async function remove(c, isReply) {
    const n = (replies[c.id] || []).length;
    const ok = await confirm({
      title: isReply ? 'Delete this reply?' : 'Delete this comment?',
      body: n ? `Its ${n === 1 ? 'reply is' : `${n} replies are`} deleted with it. This cannot be undone.` : 'This cannot be undone.',
      confirmLabel: isReply ? 'Delete reply' : 'Delete comment',
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/api/comments/${c.id}`);
      onCommentsChange();
    } catch { toast(isReply ? 'The reply was not deleted. Try again.' : 'The comment was not deleted. Try again.', { tone: 'danger' }); }
  }

  async function resolve(c) {
    try {
      await api.patch(`/api/comments/${c.id}/resolve`);
      onCommentsChange();
    } catch { toast('The comment was not updated. Try again.', { tone: 'danger' }); }
  }

  async function sendReply(c) {
    if (!replying?.text.trim() || busy) return;
    setBusy(true);
    try {
      await api.post('/api/comments', {
        videoAssetId:     c.videoAssetId,
        timestampSeconds: c.timestampSeconds,
        text:             replying.text.trim(),
        parentId:         c.id,
      });
      onCommentsChange();
      setReplying(null);
    } catch { toast('The reply did not post. Try again.', { tone: 'danger' }); }
    finally { setBusy(false); }
  }

  const editField = (c, label) => (
    <InlineEdit label={label} value={editing.text} onChange={text => setEditing({ id: c.id, text })} busy={busy} saveLabel="Save"
      onSave={saveEdit} onCancel={() => setEditing(null)} />
  );
  const ownActions = (c, isReply) => isOwn(c) && (
    <>
      <Button variant="quiet" icon={Pencil} onClick={() => { setReplying(null); setEditing({ id: c.id, text: c.text }); }}>Edit</Button>
      <Button variant="quiet" icon={Trash2} onClick={() => remove(c, isReply)}>Delete</Button>
    </>
  );

  const cardProps = card => {
    const c = card.raw;
    const thread = replies[c.id] || [];
    return {
      editor: editing?.id === c.id ? editField(c, 'Edit comment') : undefined,
      onReply: () => { setEditing(null); setReplying(r => (r?.id === c.id ? null : { id: c.id, text: '' })); },
      actions: (
        <>
          {ownActions(c, false)}
          {showResolve && (
            <>
              <span className="ui-spacer" />
              <Button variant="quiet" icon={Check} aria-pressed={!!c.resolvedAt} onClick={() => resolve(c)}>{c.resolvedAt ? 'Resolved' : 'Resolve'}</Button>
            </>
          )}
        </>
      ),
      children: (thread.length > 0 || replying?.id === c.id) ? (
        <>
          {thread.length > 0 && (
            <ul className="ui-plainlist ui-replies" aria-label={`Replies to comment ${card.n || ''}`.trim()}>
              {thread.map(r => (
                <li key={r.id} className="ui-reply">
                  <Avatar name={nameOf(r)} initials={r.commenter?.initials} />
                  <div className="ui-stack ui-stack--tight">
                    <span className="ui-cluster"><span className="ui-cc__name">{nameOf(r)}</span><span className="ui-meta">{shortDate(r.createdAt)}</span></span>
                    {editing?.id === r.id ? editField(r, 'Edit reply') : (
                      <>
                        <p className="ui-cc__text">{r.text}</p>
                        {isOwn(r) && <div className="ui-cluster">{ownActions(r, true)}</div>}
                      </>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
          {replying?.id === c.id && (
            <InlineEdit label="Reply" value={replying.text} onChange={text => setReplying({ id: c.id, text })} busy={busy} saveLabel="Reply"
              onSave={() => sendReply(c)} onCancel={() => setReplying(null)} />
          )}
        </>
      ) : undefined,
    };
  };

  return (
    <CommentsColumn
      comments={cards}
      activeId={activeCommentId}
      onSeek={(t, c) => onSeekTo?.(t, c.id)}
      cardProps={manage ? cardProps : undefined}
      details={details}
      format={format}
      fps={fps}
      emptyMessage={emptyMessage}
      composer={canPost
        ? (slot || (
          <Composer identity={identity} time={currentTimeSeconds} format={format} fps={fps} onPost={handlePost} onTypingStart={onTypingStart}
            submitOnEnter textareaRef={textareaRef} note="Typing pauses the video. Enter sends." testId="review-composer" sendTestId="review-send" />
        ))
        : slot}
    />
  );
}
